import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, BedDouble, Check, ArrowLeft } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import LazyImage from "@/components/site/LazyImage";
import BookingButtons from "@/components/site/BookingButtons";
import { Reveal, MaskLines } from "@/components/site/Reveal";
import { getRoom, ROOMS } from "@/data/rooms";
import { waLink } from "@/data/site";

export default function RoomDetail() {
  const { slug } = useParams();
  const room = getRoom(slug);
  if (!room) return <Navigate to="/stay" replace />;

  const roomMsg = `Namaste! I'd like to book the "${room.name}" at Vruksha Valley Eco Resort. Please share availability and tariff details.`;
  const others = ROOMS.filter((r) => r.slug !== slug);

  return (
    <PageShell testId="room-detail-page">
      {/* Hero image */}
      <section className="relative flex h-[70vh] min-h-[480px] items-end overflow-hidden">
        <img src={room.image} alt={room.name} className="absolute inset-0 h-full w-full object-cover" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/30 to-forest/20" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-14 md:px-10 md:pb-20">
          <Link to="/stay" data-testid="room-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-ivory/80 hover:text-ivory">
            <ArrowLeft className="h-4 w-4" /> All Stays
          </Link>
          <p className="mb-3 text-xs uppercase tracking-widest-2 text-gold md:text-sm">{room.tagline}</p>
          <h1 className="font-display text-5xl font-bold leading-[0.95] tracking-tight text-ivory sm:text-6xl md:text-7xl">
            <MaskLines lines={[room.name]} />
          </h1>
        </div>
      </section>

      <section className="bg-ivory py-12 md:py-16">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-7">
            <Reveal>
              <div className="flex flex-wrap gap-8 border-b border-charcoal/10 pb-8 text-charcoal/80">
                <span className="flex items-center gap-2"><Users className="h-5 w-5 text-brown" /> {room.capacity}</span>
                <span className="flex items-center gap-2"><BedDouble className="h-5 w-5 text-brown" /> {room.bed}</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg leading-relaxed text-charcoal/75">{room.description}</p>
            </Reveal>

            <Reveal delay={0.15}>
              <h3 className="mt-12 font-serif text-2xl text-forest">Amenities</h3>
              <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {room.amenities.map((a) => (
                  <li key={a} className="flex items-center gap-3 text-charcoal/75">
                    <Check className="h-4 w-4 text-moss" /> {a}
                  </li>
                ))}
              </ul>
            </Reveal>

            <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {room.gallery.map((g, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                >
                  <LazyImage src={g} alt={`${room.name} ${i + 1}`} className="aspect-square rounded-2xl" />
                </motion.div>
              ))}
            </div>
          </div>

          {/* Sticky booking card */}
          <div className="md:col-span-5">
            <div className="sticky top-28 rounded-2xl border border-charcoal/10 bg-card p-8 shadow-[0_8px_30px_rgba(26,54,38,0.08)]">
              <p className="text-sm uppercase tracking-widest-2 text-brown">Reserve Your Stay</p>
              <p className="mt-2 font-display text-3xl font-bold text-forest">{room.name}</p>
              <div className="my-6 h-px bg-charcoal/10" />
              <p className="text-charcoal/70">Reserve this cottage directly with us — no middlemen, instant confirmation on WhatsApp or call.</p>
              <div className="mt-6">
                <BookingButtons variant="light" size="md" className="flex-col items-stretch [&>a]:justify-center" />
              </div>
              <a
                href={waLink(roomMsg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="room-enquire-link"
                className="mt-4 block text-center text-sm text-forest underline underline-offset-4"
              >
                Enquire about {room.name}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Other stays */}
      <section className="bg-forest py-20 text-ivory md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <p className="mb-10 text-xs uppercase tracking-widest-2 text-gold md:text-sm">Other Stays</p>
          <div className="grid gap-6 md:grid-cols-2">
            {others.map((r) => (
              <Link key={r.slug} to={`/stay/${r.slug}`} data-testid={`other-room-${r.slug}`} className="group relative overflow-hidden rounded-2xl">
                <LazyImage src={r.image} alt={r.name} className="aspect-[16/9]" imgClassName="transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/85 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <h3 className="font-display text-2xl font-bold md:text-3xl">{r.name}</h3>
                  <p className="text-ivory/75">{r.tagline}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
